/** 비교 로직 훅 — IXD report 기반 비교 흐름 */

import { useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ComparisonSummaryResponse,
  compareFromReport,
  uploadAndCompare,
} from "@/apis/compare/route";
import {
  isDsdSectionNotFoundError,
  isInvalidIxdStructureError,
} from "@/apis/errors";
import {
  DSD_SECTION_NOT_FOUND_MODAL_DESCRIPTION,
  DSD_SECTION_NOT_FOUND_TITLE,
} from "@/constants/dsd-upload";
import { UPLOAD_CARD_BY_MODE } from "@/constants/ixd-upload-mode";
import { doesFileMatchAccept } from "@/utils/file-accept";
import { pushModal } from "@/stores/modal";
import { CompareFailedModal } from "@/components/modals/compare-failed-modal";
import { UploadFailedModal } from "@/components/modals/upload-failed-modal";
import queries from "@/queries";
import { buildComparisonUrl } from "@/utils/ixd-dsd-compare/comparison-url";
import {
  openPendingTab,
  resolvePendingTab,
  warnPopupBlocked,
} from "@/utils/pending-tab";

/**
 * 대사의 IXD 쪽으로 무엇을 올릴지.
 *
 * `dart-filing` 은 DART 공시 XBRL(zip) — 편집기 산출물이 아니라 공시본이라 값 편집
 * 경로가 없고, 서버가 IXD report 를 만들지 않는다. 그래서 "새 IXD 업로드"에 섞지 않고
 * 별도 모드로 둔다.
 */
export type IxdSourceMode = "upload" | "existing-report" | "dart-filing";

interface UseCompareInput {
  dsdFile: File | null;
  /** upload mode 입력 */
  ixdFile: File | null;
  /** existing-report mode 입력 */
  selectedIxdReportId: string | null;
  sourceMode: IxdSourceMode;
}

const showCompareFailedModal = () => pushModal(CompareFailedModal);

const showUploadFailedModal = () => pushModal(UploadFailedModal);

/**
 * 재무제표가 없는 DSD(사업·반기·분기보고서 본문)는 재시도해도 같은 결과라,
 * 일시적 오류 문구 대신 어떤 파일을 올려야 하는지와 문의 경로를 안내한다.
 */
const showDsdSectionNotFoundModal = () =>
  pushModal(CompareFailedModal, {
    title: DSD_SECTION_NOT_FOUND_TITLE,
    description: DSD_SECTION_NOT_FOUND_MODAL_DESCRIPTION,
    showContact: true,
  });

/**
 * 실패 오류를 안내 모달로 라우팅한다. 갈래를 테스트로 고정하려고 export 한다.
 *
 * `sourceMode` 가 필요한 이유: 변환 안내 모달 문구는 "제출용 .ixd 를 편집용으로"라,
 * 공시본(zip) 을 올린 사용자에게는 고칠 방법을 알려주지 못한다. 그쪽은 일반 실패로 보낸다.
 */
export const showFailedModal = (
  error: unknown,
  sourceMode: IxdSourceMode = "upload",
) => {
  if (sourceMode !== "dart-filing" && isInvalidIxdStructureError(error)) {
    showUploadFailedModal();
    return;
  }

  if (isDsdSectionNotFoundError(error)) {
    showDsdSectionNotFoundModal();
    return;
  }

  showCompareFailedModal();
};

export const useCompare = ({
  dsdFile,
  ixdFile,
  selectedIxdReportId,
  sourceMode,
}: UseCompareInput) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // 클릭 시 미리 연 탭 참조. 완료/실패 시 주소 주입 또는 정리에 사용.
  const pendingTabRef = useRef<Window | null>(null);

  const applyComparisonResult = (data: ComparisonSummaryResponse) => {
    // 미리 연 탭을 대사 화면 URL로 보낸다. 새 탭은 URL의 comparisonId로
    // 서버에서 summary를 새로 받아 렌더링한다(탭 간 React Query 캐시 비공유).
    const url = buildComparisonUrl(
      data.comparisonId,
      data.chunks[0]?.id ?? null,
    );
    const tab = pendingTabRef.current;
    // 대사는 이미 끝났다 — 탭을 못 열면 같은 탭 이동이라도 줘야 조용히 묻히지 않는다
    if (!resolvePendingTab(tab, url)) {
      warnPopupBlocked("대사 결과 열기", () => navigate(url));
    }
    pendingTabRef.current = null;

    // 업로드 화면의 히스토리 목록 갱신.
    queryClient.invalidateQueries({ queryKey: queries.comparisonHistory._def });
  };

  const handleCompareError = (error: unknown) => {
    // 실패 시 미리 연 탭 정리.
    pendingTabRef.current?.close();
    pendingTabRef.current = null;
    showFailedModal(error, sourceMode);
  };

  /**
   * upload mode: IXD/DSD 파일을 한 번에 업로드해 비교한다.
   * 업로드 실패와 비교 실패는 분리해서 안내 모달을 띄운다.
   */
  const compareUploadMutation = useMutation({
    mutationFn: ({ dsd, ixd }: { dsd: File; ixd: File }) =>
      uploadAndCompare(ixd, dsd),
    onSuccess: applyComparisonResult,
    onError: handleCompareError,
  });

  /**
   * existing-report mode: 기존 IXD report와 DSD를 바로 비교한다.
   */
  const compareFromExistingMutation = useMutation({
    mutationFn: ({ dsd, ixdReportId }: { dsd: File; ixdReportId: string }) =>
      compareFromReport(dsd, ixdReportId),
    onSuccess: applyComparisonResult,
    onError: handleCompareError,
  });

  const isComparing =
    compareUploadMutation.isPending || compareFromExistingMutation.isPending;

  // 파일 업로드 모드(.ixd / 공시 zip)는 입력이 같고 서버가 확장자로 갈린다.
  const isFileUploadMode = sourceMode !== "existing-report";

  /**
   * 모드와 파일의 주인이 다르다 — `ixdFile` 은 store, `sourceMode` 는 화면 local state.
   * 그래서 "모드 바꾸면 파일 비우기"를 호출부가 잊으면 `.ixd` 가 공시본 대사로 올라간다.
   * 여기서 확장자를 한 번 더 확인해 잘못된 조합이면 버튼 자체를 막는다.
   */
  const isIxdFileForMode =
    !!ixdFile &&
    isFileUploadMode &&
    doesFileMatchAccept(ixdFile, UPLOAD_CARD_BY_MODE[sourceMode].accept);

  const canCompare = isFileUploadMode
    ? !!(dsdFile && isIxdFileForMode)
    : !!(dsdFile && selectedIxdReportId);

  const handleCompare = () => {
    // 사용자 제스처 동안 탭을 먼저 확보 (팝업 차단 회피).
    const openTab = () => {
      pendingTabRef.current = openPendingTab({
        title: "대사 화면 준비 중...",
        message: "대사 화면을 준비하고 있습니다...",
      });
    };

    if (isFileUploadMode) {
      if (!dsdFile || !ixdFile || !isIxdFileForMode) return;
      openTab();
      compareUploadMutation.mutate({ dsd: dsdFile, ixd: ixdFile });
      return;
    }
    if (!dsdFile || !selectedIxdReportId) return;
    openTab();
    compareFromExistingMutation.mutate({
      dsd: dsdFile,
      ixdReportId: selectedIxdReportId,
    });
  };

  return {
    isComparing,
    handleCompare,
    canCompare,
  };
};
